<template>
  <account-layout>
    <el-row :gutter="60">
      <el-col :span="24" class="row-space">
        <el-form :model="resetPassword" :rules="rules" ref="form">
          <el-form-item prop="password" label="New Password">
            <el-input v-model="resetPassword.password" type="password" class="input-space" placeholder="Type here..." @input="updatePasswordRequirements" />
          </el-form-item>
          <!-- Password Requirements -->
          <div v-if="resetPassword.password" class="password-requirements">
            <div class="requirement-item" :class="{ 'requirement-met': passwordRequirements.minLength }">
              <i :class="passwordRequirements.minLength ? 'el-icon-check' : 'el-icon-close'"></i>
              <span>Minimum 8 characters</span>
            </div>
            <div class="requirement-item" :class="{ 'requirement-met': passwordRequirements.hasUpperCase }">
              <i :class="passwordRequirements.hasUpperCase ? 'el-icon-check' : 'el-icon-close'"></i>
              <span>At least one uppercase letter</span>
            </div>
            <div class="requirement-item" :class="{ 'requirement-met': passwordRequirements.hasLowerCase }">
              <i :class="passwordRequirements.hasLowerCase ? 'el-icon-check' : 'el-icon-close'"></i>
              <span>At least one lowercase letter</span>
            </div>
            <div class="requirement-item" :class="{ 'requirement-met': passwordRequirements.hasSpecialChar }">
              <i :class="passwordRequirements.hasSpecialChar ? 'el-icon-check' : 'el-icon-close'"></i>
              <span>At least one special character</span>
            </div>
            <div class="requirement-item" :class="{ 'requirement-met': passwordRequirements.noSpaces }">
              <i :class="passwordRequirements.noSpaces ? 'el-icon-check' : 'el-icon-close'"></i>
              <span>No spaces allowed</span>
            </div>
          </div>
          <el-form-item prop="confirmPassword" label="Confirm New Password">
            <el-input v-model="resetPassword.confirmPassword" type="password" class="input-space" placeholder="Type here..." />
          </el-form-item>
        </el-form>
      </el-col>
    </el-row>
    <el-row class="row-space">
      <el-col :span="24">
        <el-button
          type="primary"
          class="ac-btn"
          @click="reset"
        >Reset Password</el-button>
      </el-col>
    </el-row>
    <el-row>
      <el-col :span="24" class="align-center">
        <el-button type="text" @click="login">Return to Login</el-button>
      </el-col>
    </el-row>
  </account-layout>
</template>

<script>
import _ from 'lodash';
import { errormsg } from '@/utils/constants';

import AccountLayout from './AccountLayout.vue';

export default {
  name: 'AccountReset',
  components: {
    AccountLayout
  },
  props: {
    platform: String
  },
  data() {
    const passwordRule = {
      required: true,
      trigger: 'blur',
      message: errormsg.password,
      validator: (r, value, c) => {
        let valid = true;
        if (!value) valid = false;
        // min length 8
        if (value && value.length < 8) valid = false;
        // any upper case (check only letters)
        if (!_.some(value, (t) => /[A-Z]/.test(t))) valid = false;
        // any lower case (check only letters)
        if (!_.some(value, (t) => /[a-z]/.test(t))) valid = false;
        // any special case
        if (!new RegExp('([`~\\!@#\\$%\\^\\&\\*\\(\\)\\-_\\=\\+\\[\\{\\}\\]\\\\|;:\\\'",<.>\\/\\?€£¥₹§±].*)').test(value)) valid = false;
        // not any space
        if (_.some(value, (t) => t === ' ')) valid = false;

        if (valid) return c();

        return c(new Error(errormsg.password));
      }
    };

    return {
      resetPassword: {
        password: undefined,
        confirmPassword: undefined
      },
      rules: {
        password: passwordRule,
        confirmPassword: passwordRule,
      },
      passwordRequirements: {
        minLength: false,
        hasUpperCase: false,
        hasLowerCase: false,
        hasSpecialChar: false,
        noSpaces: true
      }
    };
  },
  methods: {
    reset() {
      this.$refs.form.validate()
        .then((val) => {
          if (!val) return;
          const { token } = this.$route.params;
          if (!token) {
            this.$message.error('Invalid token');
            return;
          }

          if (this.resetPassword.password !== this.resetPassword.confirmPassword) {
            this.$message.error('The new password and confirmation password do not match');
            return;
          }

          this.$store.commit('loading', { loading: true, text: 'Loading...' });
          this.$httpNoAuth.post(`assemble/api/v1/account/${this.platform}/reset/submit`, { hash: token, password: this.resetPassword.password })
            .then(() => {
              this.$message.success('You password has been changed successfully!');
              this.$store.commit('loading', false);
              this.$router.push('/login');
            }).catch((e) => {
              this.$message.error(e.response.data.message);
              this.$store.commit('loading', false);
            });
        })
        .catch(() => {});
    },
    login() {
      this.$router.push('/login');
    },
    updatePasswordRequirements() {
      const value = this.resetPassword.password;
      if (!value) {
        this.passwordRequirements = {
          minLength: false,
          hasUpperCase: false,
          hasLowerCase: false,
          hasSpecialChar: false,
          noSpaces: true
        };
        return;
      }

      this.passwordRequirements = {
        minLength: value.length >= 8,
        hasUpperCase: /[A-Z]/.test(value),
        hasLowerCase: /[a-z]/.test(value),
        hasSpecialChar: new RegExp('([`~\\!@#\\$%\\^\\&\\*\\(\\)\\-_\\=\\+\\[\\{\\}\\]\\\\|;:\\\'",<.>\\/\\?€£¥₹§±].*)').test(value),
        noSpaces: !value.includes(' ')
      };
    }
  }
};
</script>

<style scoped lang="scss">
.content-card {
  margin-left: 1em;
  margin-right: 1em;
  margin-top: 2em;
  width: 90%;
  max-width: 30em;
}

.align-center {
  text-align: center;
}

.row-space {
  margin-top: 1em;
}

.input-space {
  margin-top: 0.5em;
}

.ac-btn {
  width: 100%;
  background-color: $highlight !important;
  border: 0;
}

.password-requirements {
  background-color: #f5f7fa;
  border-radius: 4px;
  padding: 12px;
  font-size: 14px;
  margin-top: 10px;
  margin-bottom: 15px;
}

.requirement-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  color: #909399;

  &:last-child {
    margin-bottom: 0;
  }

  i {
    margin-right: 8px;
    font-size: 16px;
    color: #f56c6c;
  }

  &.requirement-met {
    color: #606266;

    i {
      color: #67c23a;
    }
  }
}
</style>
